// SEO 工具函数 - GEO 整改项
// 提供 JSON-LD 结构化数据注入能力

const SITE_ORIGIN = 'https://www.smalleel.com'

/**
 * 注入或更新一个 JSON-LD 结构化数据
 * @param {string} type 唯一标识(同 id 重复注入会覆盖)
 * @param {object} data 结构化数据对象
 */
export function setJsonLd(type, data) {
  const id = `jsonld-${type}`
  let script = document.getElementById(id)
  if (script) script.remove()
  if (!data) return
  script = document.createElement('script')
  script.type = 'application/ld+json'
  script.id = id
  script.text = JSON.stringify(data)
  document.head.appendChild(script)
}

/**
 * 移除一个 JSON-LD
 * @param {string} type
 */
export function clearJsonLd(type) {
  const id = `jsonld-${type}`
  const script = document.getElementById(id)
  if (script) script.remove()
}

/**
 * 注入面包屑导航 JSON-LD
 * @param {Array<{name: string, url: string}>} items 面包屑层级,顺序:根 -> 当前
 */
export function injectBreadcrumb(items) {
  if (!Array.isArray(items) || items.length === 0) return
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url
    }))
  }
  setJsonLd('breadcrumb', data)
}

/**
 * 注入 WebPage 类型 JSON-LD(通用列表页)
 * @param {object} opts
 *   - id: 页面 @id
 *   - name: 页面名称
 *   - description: 描述
 *   - url: 页面URL
 *   - inLanguage: 语言
 *   - isPartOf: 站点顶层 @id
 *   - breadcrumb: 面包屑items
 */
export function injectWebPage(opts) {
  if (!opts) return
  const data = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': opts.id,
    'name': opts.name,
    'description': opts.description,
    'url': opts.url,
    'inLanguage': opts.inLanguage || 'zh-CN',
    'isPartOf': {
      '@type': 'WebSite',
      '@id': `${SITE_ORIGIN}/#website`,
      'name': '辰鳗科技集团-小鳗管家',
      'url': `${SITE_ORIGIN}/`
    }
  }
  if (opts.breadcrumb && opts.breadcrumb.length) {
    data.breadcrumb = {
      '@type': 'BreadcrumbList',
      'itemListElement': opts.breadcrumb.map((item, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': item.url
      }))
    }
  }
  setJsonLd('webpage', data)
}

/**
 * 注入 Article 类型 JSON-LD(新闻详情页)
 */
export function injectArticle(opts) {
  if (!opts) return
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': opts.id,
    'headline': opts.headline,
    'description': opts.description,
    'url': opts.url,
    'datePublished': opts.datePublished,
    'dateModified': opts.dateModified || opts.datePublished,
    'inLanguage': 'zh-CN',
    'author': {
      '@type': 'Organization',
      'name': '辰鳗科技集团有限公司',
      'url': SITE_ORIGIN
    },
    'publisher': {
      '@type': 'Organization',
      'name': '辰鳗科技集团有限公司',
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_ORIGIN}/static/logo.png`
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': opts.url
    },
    'image': opts.image || `${SITE_ORIGIN}/static/logo.png`
  }
  if (opts.breadcrumb && opts.breadcrumb.length) {
    data.breadcrumb = {
      '@type': 'BreadcrumbList',
      'itemListElement': opts.breadcrumb.map((item, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': item.url
      }))
    }
  }
  setJsonLd('article', data)
}

/**
 * 注入 Product 类型 JSON-LD(产品页)
 */
export function injectProduct(opts) {
  if (!opts) return
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': opts.id,
    'name': opts.name,
    'description': opts.description,
    'image': opts.image || `${SITE_ORIGIN}/static/logo.png`,
    'brand': {
      '@type': 'Brand',
      'name': '辰鳗科技-小鳗管家'
    },
    'manufacturer': {
      '@type': 'Organization',
      'name': '辰鳗科技集团有限公司',
      'url': SITE_ORIGIN
    },
    'category': opts.category || '能源管理系统',
    'url': opts.url
  }
  if (opts.sku) data.sku = opts.sku
  if (opts.offers) {
    data.offers = {
      '@type': 'Offer',
      'url': opts.url,
      'priceCurrency': 'CNY',
      'price': opts.offers.price || '0',
      'priceValidUntil': opts.offers.priceValidUntil || '2030-12-31',
      'availability': 'https://schema.org/InStock',
      'seller': {
        '@type': 'Organization',
        'name': '辰鳗科技集团有限公司'
      }
    }
  }
  setJsonLd('product', data)
}

/**
 * 注入 ItemList(列表/产品/方案)JSON-LD
 */
export function injectItemList(opts) {
  if (!opts) return
  const data = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': opts.id,
    'name': opts.name,
    'description': opts.description,
    'url': opts.url,
    'numberOfItems': opts.items ? opts.items.length : 0,
    'itemListElement': (opts.items || []).map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': item.type || 'Service',
        'name': item.name,
        'description': item.description,
        'url': item.url,
        'image': item.image
      }
    }))
  }
  setJsonLd('itemlist', data)
}

/**
 * 注入 FAQPage JSON-LD
 * @param {Array<{question: string, answer: string}>} faqs
 */
export function injectFAQ(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return
  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': f.answer
      }
    }))
  }
  setJsonLd('faq', data)
}

export { SITE_ORIGIN }



// WEBPACK FOOTER //
// ./src/untils/seo.js